import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import AccessibleButton from '../components/common/AccessibleButton';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import StatusBadge from '../components/common/StatusBadge';

const Tasks = () => {
    const [loading, setLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    const [selected, setSelected] = useState(null);
    const [details, setDetails] = useState(null);
    const [draft, setDraft] = useState('');
    const [saveState, setSaveState] = useState('idle');
    const saveTimer = useRef(null);
    const latestDraft = useRef('');

    const loadTasks = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get('/learning/tasks');
            setTasks(response.data.data?.tasks || []);
        } catch (error) {
            toast.error('Unable to load practical tasks');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTasks();
    }, [loadTasks]);

    const openTask = async (task) => {
        if (task.status === 'locked') {
            toast.error('This task is locked. Complete earlier modules first.');
            return;
        }
        setSelected(task);
        setSaveState('idle');
        try {
            const response = await axios.get(`/learning/item/${task.id}`);
            const data = response.data.data;
            setDetails(data);
            const draftNote = data.progress?.find((p) => p.section === 'draft')?.metadata?.notes || '';
            setDraft(draftNote);
            latestDraft.current = draftNote;
            if (task.status === 'available') {
                await axios.post(`/learning/item/${task.id}/start`);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Unable to open this task');
            setSelected(null);
        }
    };

    const autosave = useCallback((value) => {
        if (!selected) return;
        latestDraft.current = value;
        setSaveState('saving');
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            const clientUpdatedAt = new Date().toISOString();
            try {
                await axios.post(`/learning/item/${selected.id}/progress`, {
                    section: 'draft',
                    progress_percentage: details?.assignment?.progress_percentage || 10,
                    metadata: {
                        autosave: true,
                        notes: latestDraft.current,
                        clientUpdatedAt
                    }
                });
                setSaveState('saved');
            } catch (error) {
                setSaveState('error');
            }
        }, 700);
    }, [selected, details]);

    const onDraftChange = (event) => {
        const value = event.target.value;
        setDraft(value);
        autosave(value);
    };

    const submitTask = async () => {
        if (!selected) return;
        try {
            await axios.post(`/learning/item/${selected.id}/progress`, {
                section: 'submission',
                progress_percentage: 100,
                completed: true,
                metadata: { notes: draft, submitted: true }
            });
            toast.success('Task submitted');
            setSelected(null);
            loadTasks();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to submit task');
        }
    };

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-48" />
                <div className="skeleton h-24" />
                <div className="skeleton h-24" />
                <span className="sr-only">Loading your practical tasks…</span>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6 overflow-x-hidden">
            <PageHeader
                title="Tasks"
                description="Practical tasks from your learning journey. Drafts autosave while you work."
            />

            {!selected ? (
                <div className="space-y-3">
                    {tasks.length === 0 ? (
                        <EmptyState
                            icon={ClipboardDocumentListIcon}
                            title="No practical tasks yet"
                            description="Check Learning & Training for modules that unlock tasks."
                            action={
                                <Link to="/learning" className="btn-secondary">
                                    Open Learning & Training →
                                </Link>
                            }
                        />
                    ) : (
                        tasks.map((task) => (
                            <button
                                key={task.id}
                                type="button"
                                onClick={() => openTask(task)}
                                className="w-full text-left card-interactive min-h-[44px] animate-fade-up"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <h2 className="font-semibold text-ink-950">{task.title}</h2>
                                        <p className="text-sm text-ink-600 mt-1">{task.description}</p>
                                        {task.requiredHours != null && (
                                            <p className="text-xs text-ink-500 mt-2">Expected hours: {task.requiredHours}</p>
                                        )}
                                    </div>
                                    <StatusBadge status={task.status} label={task.studentStatus} />
                                </div>
                            </button>
                        ))
                    )}
                </div>
            ) : (
                <div className="card space-y-4 animate-fade-up">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div>
                            <h2 className="text-xl font-semibold text-ink-950">{selected.title}</h2>
                            <p className="text-ink-600 mt-1">{selected.description}</p>
                        </div>
                        <AccessibleButton variant="outline" onClick={() => setSelected(null)}>Back to tasks</AccessibleButton>
                    </div>

                    {details?.item?.learning_outcomes && (
                        <div>
                            <h3 className="text-sm font-semibold text-ink-950">Expected outcome</h3>
                            <p className="text-sm text-ink-600 mt-1">{details.item.learning_outcomes}</p>
                        </div>
                    )}

                    <div>
                        <div className="flex items-center justify-between gap-2">
                            <label className="label" htmlFor="task-draft">Your work notes</label>
                            <span className="text-xs text-ink-500" aria-live="polite">
                                {saveState === 'saving' && 'Saving...'}
                                {saveState === 'saved' && 'Saved'}
                                {saveState === 'error' && 'Save failed — will retry'}
                            </span>
                        </div>
                        <textarea
                            id="task-draft"
                            className="input-field min-h-[160px]"
                            value={draft}
                            onChange={onDraftChange}
                            placeholder="Capture observations, links, and progress here. Drafts autosave."
                        />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <AccessibleButton
                            variant="outline"
                            onClick={() => {
                                autosave(draft);
                                setSelected(null);
                                toast.success('Draft saved — you can continue later');
                            }}
                        >
                            Save & return
                        </AccessibleButton>
                        <AccessibleButton onClick={submitTask}>
                            Submit task
                        </AccessibleButton>
                        <Link to="/evidence" className="btn-secondary justify-center min-h-[44px]">
                            Attach evidence →
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Tasks;
